import { useState } from 'react'
import type { Meta, StoryObj } from '@storybook/react'
import { expect, userEvent, within } from '@storybook/test'

import RecipientFields from './RecipientsField'

type DistributionList = {
  id: string
  name: string
  recipients: string[]
}

const engineeringList: DistributionList = {
  id: 'engineering',
  name: 'Engineering',
  recipients: ['a@example.com', 'b@example.com', 'c@example.com'],
}

const operationsList: DistributionList = {
  id: 'operations',
  name: 'Operations',
  recipients: ['d@example.com', 'e@example.com'],
}

const sharedList: DistributionList = {
  id: 'shared',
  name: 'Shared',
  recipients: ['a@example.com', 'z@example.com'],
}

const distributionLists: DistributionList[] = [
  engineeringList,
  operationsList,
  sharedList,
]

type HarnessProps = {
  initialSelectedDistributionLists?: DistributionList[]
  initialRecipients?: string[]
  required?: boolean
  error?: boolean
}

function RecipientFieldsHarness({
  initialSelectedDistributionLists = [],
  initialRecipients = [],
  required = false,
  error = false,
}: HarnessProps) {
  const [selectedDistributionLists, setSelectedDistributionLists] = useState(
    initialSelectedDistributionLists
  )
  const [recipients, setRecipients] = useState(initialRecipients)

  return (
    <RecipientFields
      distributionLists={distributionLists}
      selectedDistributionLists={selectedDistributionLists}
      setSelectedDistributionLists={setSelectedDistributionLists}
      recipients={recipients}
      setRecipients={setRecipients}
      required={required}
      error={error}
    />
  )
}

const meta = {
  title: 'UI/RecipientsField',
  component: RecipientFieldsHarness,
  args: {
    initialSelectedDistributionLists: [],
    initialRecipients: [],
    required: false,
    error: false,
  },
} satisfies Meta<typeof RecipientFieldsHarness>

export default meta

type Story = StoryObj<typeof meta>

const getCanvas = (canvasElement: HTMLElement) => within(canvasElement)

const openGroupLists = async (canvasElement: HTMLElement) => {
  const canvas = getCanvas(canvasElement)
  await userEvent.click(canvas.getByLabelText('Group Lists'))
}

const getOpenGroupsMenuElement = (canvasElement: HTMLElement) => {
  const page = within(canvasElement.ownerDocument.body)

  const menu =
    page.queryByRole('listbox') ??
    page.queryByRole('menu')

  if (!menu) {
    throw new Error('Could not find open Groups List menu')
  }

  return menu as HTMLElement
}

const getOpenGroupsMenu = (canvasElement: HTMLElement) => {
  return within(getOpenGroupsMenuElement(canvasElement))
}

const selectGroupFromOpenMenu = async (
  canvasElement: HTMLElement,
  groupName: string
) => {
  const menu = getOpenGroupsMenu(canvasElement)

  const option =
    menu.queryByRole('option', { name: groupName }) ??
    menu.queryByRole('menuitemcheckbox', { name: groupName }) ??
    menu.getByText(groupName)

  await userEvent.click(option)
}

const closeGroupsMenu = async (canvasElement: HTMLElement) => {
  const menuElement = getOpenGroupsMenuElement(canvasElement)
  menuElement.focus()
  await userEvent.keyboard('{Escape}')
}

const addManualRecipient = async (
  canvasElement: HTMLElement,
  email: string
) => {
  const canvas = getCanvas(canvasElement)
  const input = canvas.getByLabelText('Email(s)')

  await userEvent.click(input)
  await userEvent.type(input, `${email}{enter}`)
}

const clickChipDelete = async (
  canvasElement: HTMLElement,
  chipText: string,
  occurrence = 0
) => {
  const canvas = getCanvas(canvasElement)
  const matchingTextNodes = canvas.getAllByText(chipText)
  const chipTextNode = matchingTextNodes[occurrence]

  const chipRoot =
    chipTextNode.closest('.MuiChip-root') ??
    chipTextNode.closest('[class*="MuiChip-root"]') ??
    chipTextNode.parentElement

  if (!chipRoot) {
    throw new Error(`Could not find chip root for "${chipText}"`)
  }

  const deleteControl =
    chipRoot.querySelector('.MuiChip-deleteIcon') ??
    chipRoot.querySelector('[class*="MuiChip-deleteIcon"]') ??
    chipRoot.querySelector('svg')

  if (!deleteControl) {
    throw new Error(`Could not find delete control for "${chipText}"`)
  }

  await userEvent.click(deleteControl as HTMLElement)
}

export const Default: Story = {}

export const CheckMultipleGroups: Story = {
  play: async ({ canvasElement }) => {
    const canvas = getCanvas(canvasElement)

    await openGroupLists(canvasElement)
    await selectGroupFromOpenMenu(canvasElement, 'Engineering')
    await selectGroupFromOpenMenu(canvasElement, 'Operations')
    await closeGroupsMenu(canvasElement)

    await expect(canvas.getByText('a@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('b@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('c@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('d@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('e@example.com')).toBeInTheDocument()
  },
}

export const RemoveGroup: Story = {
  args: {
    initialSelectedDistributionLists: [engineeringList],
    initialRecipients: [],
  },
  play: async ({ canvasElement }) => {
    const canvas = getCanvas(canvasElement)

    await expect(canvas.getByText('a@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('b@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('c@example.com')).toBeInTheDocument()

    await openGroupLists(canvasElement)
    await selectGroupFromOpenMenu(canvasElement, 'Engineering')
    await closeGroupsMenu(canvasElement)

    await expect(canvas.queryByText('a@example.com')).not.toBeInTheDocument()
    await expect(canvas.queryByText('b@example.com')).not.toBeInTheDocument()
    await expect(canvas.queryByText('c@example.com')).not.toBeInTheDocument()
  },
}

export const RemoveMemberOfGroup: Story = {
  args: {
    initialSelectedDistributionLists: [engineeringList],
    initialRecipients: [],
  },
  play: async ({ canvasElement }) => {
    const canvas = getCanvas(canvasElement)

    await expect(canvas.getByText('a@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('b@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('c@example.com')).toBeInTheDocument()

    await clickChipDelete(canvasElement, 'b@example.com')

    await expect(canvas.getByText('a@example.com')).toBeInTheDocument()
    await expect(canvas.queryByText('b@example.com')).not.toBeInTheDocument()
    await expect(canvas.getByText('c@example.com')).toBeInTheDocument()
  },
}

export const DeduplicatesWhenGroupMemberRemovalConvertsToManualRecipients: Story = {
  args: {
    initialSelectedDistributionLists: [engineeringList],
    initialRecipients: ['a@example.com'],
  },
  play: async ({ canvasElement }) => {
    const canvas = getCanvas(canvasElement)

    await expect(canvas.getByText('b@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('c@example.com')).toBeInTheDocument()

    await clickChipDelete(canvasElement, 'b@example.com')

    await expect(canvas.getAllByText('a@example.com')).toHaveLength(1)
    await expect(canvas.getAllByText('c@example.com')).toHaveLength(1)
    await expect(canvas.queryByText('b@example.com')).not.toBeInTheDocument()
  },
}

export const ManualRecipientsAreDeduped: Story = {
  play: async ({ canvasElement }) => {
    const canvas = getCanvas(canvasElement)

    await addManualRecipient(canvasElement, 'A@example.com')
    await addManualRecipient(canvasElement, 'a@example.com')

    await expect(canvas.getAllByText('a@example.com')).toHaveLength(1)
  },
}

export const OverlappingGroupsCanCoexist: Story = {
  play: async ({ canvasElement }) => {
    const canvas = getCanvas(canvasElement)

    await openGroupLists(canvasElement)
    await selectGroupFromOpenMenu(canvasElement, 'Engineering')
    await selectGroupFromOpenMenu(canvasElement, 'Shared')
    await closeGroupsMenu(canvasElement)

    await expect(canvas.getAllByText('a@example.com')).toHaveLength(2)
    await expect(canvas.getByText('b@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('c@example.com')).toBeInTheDocument()
    await expect(canvas.getByText('z@example.com')).toBeInTheDocument()
  },
}
